import { useContext } from "react";

import { useTranslation } from "react-i18next";

import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import {
    doesAccessLevelAllowToManageUser,
    isInternalTypeCompany
} from "shared/functions";
import { USER_ACCESS_LEVELS } from "shared/types/enums";
import { TranslatedUserAccessLevelData } from "shared/types/interfaces";

export const useUserAccessLevels = () => {
    const { rootCompanyType } = useContext(ParamsContext);

    const { isAdminManagerUser, user } = useContext(AuthContext);

    const { t } = useTranslation();

    const TRANSLATED_USER_ACCESS_LEVELS: TranslatedUserAccessLevelData[] = [
        {
            name: t(`General##${USER_ACCESS_LEVELS.User}`),
            id: USER_ACCESS_LEVELS.User
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.Support}`),
            id: USER_ACCESS_LEVELS.Support
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.Admin}`),
            id: USER_ACCESS_LEVELS.Admin
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.AdminManager}`),
            id: USER_ACCESS_LEVELS.AdminManager
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.InternalIntegration}`),
            id: USER_ACCESS_LEVELS.InternalIntegration
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.SystemUser}`),
            id: USER_ACCESS_LEVELS.SystemUser
        },
        {
            name: t(`General##${USER_ACCESS_LEVELS.SuperUser}`),
            id: USER_ACCESS_LEVELS.SuperUser
        }
    ];

    const getUserAccessLevelInfo = (accessLevel: USER_ACCESS_LEVELS) => {
        for (const userAccessLevel of [
            ...TRANSLATED_USER_ACCESS_LEVELS
        ].reverse()) {
            if (accessLevel >= userAccessLevel.id) {
                return userAccessLevel;
            }
        }

        return TRANSLATED_USER_ACCESS_LEVELS[0];
    };

    const isAllowedToAssignAccessLevel = () => {
        const isRootInternalTypeCompany =
            isInternalTypeCompany(rootCompanyType);

        return isAdminManagerUser && isRootInternalTypeCompany;
    };

    const getUserAccessLevelOptions = () => {
        const internalAccessLevels = TRANSLATED_USER_ACCESS_LEVELS.filter(
            ({ id }) => id !== USER_ACCESS_LEVELS.User
        );

        return internalAccessLevels.filter(({ id }) =>
            doesAccessLevelAllowToManageUser(user!, id)
        );
    };

    return {
        TRANSLATED_USER_ACCESS_LEVELS,
        getUserAccessLevelInfo,
        isAllowedToAssignAccessLevel,
        getUserAccessLevelOptions
    };
};
